import { PositionUpdateSourceEnum } from '@talos/kyoko';
import type { PositionsTableFilter } from '../types';

export const PositionsSourceFilterMode = {
  Internal: PositionUpdateSourceEnum.Internal,
  Gateway: PositionUpdateSourceEnum.Gateway,
  Both: 'Both',
} as const;

export type PositionsSourceFilterMode = (typeof PositionsSourceFilterMode)[keyof typeof PositionsSourceFilterMode];

export interface UnifiedSourceStreamConfig {
  subscribeInternalPositions: boolean;
  subscribeGatewayPositions: boolean;
  subscribeGatewayBalances: boolean;
}

interface UnifiedSourceStreams<T> {
  internalPositions: T;
  gatewayPositions: T;
  gatewayBalances: T;
}

export function selectUnifiedSourceStreams<T>(
  config: UnifiedSourceStreamConfig,
  streams: UnifiedSourceStreams<T>
): T[] {
  const selectedStreams: T[] = [];

  if (config.subscribeInternalPositions) {
    selectedStreams.push(streams.internalPositions);
  }
  if (config.subscribeGatewayPositions) {
    selectedStreams.push(streams.gatewayPositions);
  }
  if (config.subscribeGatewayBalances) {
    selectedStreams.push(streams.gatewayBalances);
  }

  return selectedStreams;
}

export function getUnifiedSourceFilterOptions(isTalosAdmin: boolean): PositionsSourceFilterMode[] {
  const options: PositionsSourceFilterMode[] = [PositionsSourceFilterMode.Internal, PositionsSourceFilterMode.Gateway];

  if (isTalosAdmin) {
    options.push(PositionsSourceFilterMode.Both);
  }

  return options;
}

export function getUnifiedSourceRequestTag(
  tag: string,
  source: PositionsTableFilter['Source'] | undefined,
  isTalosAdmin: boolean
): string {
  return `${tag}/${normalizePersistedSourceFilter(source, isTalosAdmin)}`;
}

export function normalizePersistedSourceFilter(
  source: PositionsTableFilter['Source'] | undefined,
  isTalosAdmin: boolean
): PositionsSourceFilterMode {
  if (source === PositionsSourceFilterMode.Both && !isTalosAdmin) {
    return PositionsSourceFilterMode.Gateway;
  }

  return source ?? PositionsSourceFilterMode.Gateway;
}

export function resolveUnifiedSourceStreamConfig(
  source: PositionsTableFilter['Source'] | undefined,
  isTalosAdmin: boolean
): UnifiedSourceStreamConfig {
  const mode = normalizePersistedSourceFilter(source, isTalosAdmin);

  switch (mode) {
    case PositionsSourceFilterMode.Internal:
      return {
        subscribeInternalPositions: true,
        subscribeGatewayPositions: false,
        subscribeGatewayBalances: false,
      };
    case PositionsSourceFilterMode.Both:
      return {
        subscribeInternalPositions: true,
        subscribeGatewayPositions: true,
        subscribeGatewayBalances: true,
      };
    case PositionsSourceFilterMode.Gateway:
    default:
      return {
        subscribeInternalPositions: false,
        subscribeGatewayPositions: true,
        subscribeGatewayBalances: true,
      };
  }
}
